import type {
  ClientSubscriptionUpdateInput,
  ClientSuspendInput,
  FEATURE_CATALOG,
  FeatureDefaults,
  FeatureFlags,
  FeatureOverridesUpdate,
  PlatformMetricsReport,
  SuperAdminAiUsageReport,
  SuperAdminClientDetail,
} from '@repo/types';
import { axiosInstance } from '../axios-instance';
import { ENDPOINTS } from '../endpoint';

/** Fiche client de l'espace /superadmin. */
export class SuperAdminClientsService {
  async getClient(id: number): Promise<SuperAdminClientDetail> {
    const { data } = await axiosInstance.get<{ data: SuperAdminClientDetail }>(ENDPOINTS.SUPERADMIN.CLIENT(id));
    return data.data;
  }

  async updateSubscription(id: number, input: ClientSubscriptionUpdateInput): Promise<SuperAdminClientDetail> {
    const { data } = await axiosInstance.patch<{ data: SuperAdminClientDetail }>(ENDPOINTS.SUPERADMIN.CLIENT_SUBSCRIPTION(id), input);
    return data.data;
  }

  async suspend(id: number, input: ClientSuspendInput): Promise<SuperAdminClientDetail> {
    const { data } = await axiosInstance.post<{ data: SuperAdminClientDetail }>(ENDPOINTS.SUPERADMIN.CLIENT_SUSPEND(id), input);
    return data.data;
  }

  async impersonate(userId: number): Promise<{ token: string; expiresAt: string; user: { id: number; email: string | null; nom: string | null; prenom: string | null } }> {
    const { data } = await axiosInstance.post(ENDPOINTS.SUPERADMIN.IMPERSONATE(userId));
    return data.data;
  }

  async updateClientFeatures(id: number, overrides: FeatureOverridesUpdate): Promise<SuperAdminClientDetail> {
    const { data } = await axiosInstance.put<{ data: SuperAdminClientDetail }>(ENDPOINTS.SUPERADMIN.CLIENT_FEATURES(id), overrides);
    return data.data;
  }

  async getFeatureDefaults(): Promise<{ catalog: typeof FEATURE_CATALOG; defaults: FeatureDefaults }> {
    const { data } = await axiosInstance.get(ENDPOINTS.SUPERADMIN.FEATURES);
    return data.data;
  }

  async updateFeatureDefaults(defaults: FeatureDefaults): Promise<{ catalog: typeof FEATURE_CATALOG; defaults: FeatureDefaults }> {
    const { data } = await axiosInstance.put(ENDPOINTS.SUPERADMIN.FEATURES, defaults);
    return data.data;
  }

  /** Consommation et coût estimé de l'IA par client pour un mois (AAAA-MM). */
  async getAiUsage(month: string): Promise<SuperAdminAiUsageReport> {
    const { data } = await axiosInstance.get<{ data: SuperAdminAiUsageReport }>(ENDPOINTS.SUPERADMIN.AI_USAGE, { params: { month } });
    return data.data;
  }

  /** Indicateurs de la plateforme (MRR, clients, activité). */
  async getMetrics(): Promise<PlatformMetricsReport> {
    const { data } = await axiosInstance.get<{ data: PlatformMetricsReport }>(ENDPOINTS.SUPERADMIN.METRICS);
    return data.data;
  }

  /** Fonctionnalités de l'organisation de l'utilisateur connecté. */
  async getMyFeatures(): Promise<FeatureFlags> {
    const { data } = await axiosInstance.get<{ data: FeatureFlags }>(ENDPOINTS.FEATURES.ME);
    return data.data;
  }

  async reactivate(id: number): Promise<SuperAdminClientDetail> {
    const { data } = await axiosInstance.post<{ data: SuperAdminClientDetail }>(ENDPOINTS.SUPERADMIN.CLIENT_REACTIVATE(id));
    return data.data;
  }
}

export const superAdminClientsService = new SuperAdminClientsService();
